import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, BookOpen } from "lucide-react";
import { usePublicLang, LangToggle } from "../lib/publicLang";
import LexiconContent from "../components/LexiconContent";
import usePageMeta from "../hooks/usePageMeta";

const T = {
  fr: {
    kicker: "Lexique",
    title: "Lexique ANATRADEX",
    subtitle: "Tous les termes techniques de l'outil — validation statistique (PBO, DSR, permutations...) et terminologie propriétaire des signaux (ZII, CSD, RL...) — expliqués simplement, puis techniquement.",
    back: "Retour",
    metaTitle: "Lexique — ANATRADEX | PBO, Deflated Sharpe, ZII, CSD expliqués",
    metaDesc: "Le lexique complet d'ANATRADEX : validation statistique de backtests (PBO, DSR, Purged K-Fold, p-value) et terminologie propriétaire des signaux (ZII, ZRI, CSD, RL), sans jargon.",
  },
  en: {
    kicker: "Glossary",
    title: "ANATRADEX Glossary",
    subtitle: "Every technical term used in the tool — statistical validation (PBO, DSR, permutations...) and proprietary signal terminology (ZII, CSD, RL...) — explained in plain terms first, then technically.",
    back: "Back",
    metaTitle: "Glossary — ANATRADEX | PBO, Deflated Sharpe, ZII, CSD explained",
    metaDesc: "The full ANATRADEX glossary: backtest statistical validation (PBO, DSR, Purged K-Fold, p-value) and proprietary signal terminology (ZII, ZRI, CSD, RL), jargon-free.",
  },
};

export default function LexiquePublic() {
  const [lang, setLang] = usePublicLang();
  const L = T[lang] || T.fr;

  usePageMeta({
    title: L.metaTitle,
    description: L.metaDesc,
    canonical: "https://anatradex.com/lexique",
    og: { title: L.metaTitle, description: L.metaDesc, url: "https://anatradex.com/lexique", type: "article" },
  });

  return (
    <div className="min-h-screen bg-bg text-white" data-testid="lexique-public-page">
      <nav className="border-b border-white/10 sticky top-0 backdrop-blur-lg bg-bg/80 z-50">
        <div className="max-w-5xl mx-auto px-6 py-4 flex justify-between items-center">
          <Link to="/" className="flex items-center gap-2">
            <img src="/favicon.svg" alt="ANATRADEX" className="w-9 h-9 rounded-lg" width="36" height="36" />
            <div>
              <div className="font-heading font-bold text-lg leading-none">ANATRADEX</div>
              <div className="text-[10px] text-white/50 uppercase tracking-widest">{L.kicker}</div>
            </div>
          </Link>
          <div className="flex items-center gap-4">
            <LangToggle lang={lang} setLang={setLang} />
            <Link to="/" className="text-sm text-white/60 hover:text-white flex items-center gap-1.5" data-testid="lexique-back-link">
              <ArrowLeft size={14} /> {L.back}
            </Link>
          </div>
        </div>
      </nav>

      <div className="max-w-5xl mx-auto px-6 py-12">
        <div className="flex items-center gap-3 mb-2">
          <BookOpen size={24} className="text-accent-blue" />
          <h1 className="font-heading font-bold text-3xl md:text-4xl tracking-tight" data-testid="lexique-title">{L.title}</h1>
        </div>
        <p className="text-white/50 text-sm mb-8 max-w-3xl">{L.subtitle}</p>
        <LexiconContent lang={lang} />
      </div>
    </div>
  );
}
